/**
 * Unseen shares (SB-5) — one definition of "new to me", shared by the toolbar
 * badge and the switcher chip so the two can never disagree.
 *
 * Sharing was a pure ACL write: no email (deliberate in v1) and no in-product
 * signal beyond a permanent "Shared" role chip that looks identical on day 1 and
 * day 100. The data to fix it already reached the client and was discarded —
 * `memberSince` on each member row, and now a per-user `sharesSeenAt` watermark.
 *
 * Pure: no I/O, no DOM. The race-safety rules live here as code rather than as
 * a comment at each call site.
 */

/** A project row is a share when someone else owns it and we joined it. */
export function isShare(project) {
  return project?.role === 'member' && Number.isFinite(Number(project?.memberSince));
}

/**
 * Shares granted after the watermark.
 * A null/absent watermark means "never looked", so every share is unseen — the
 * correct first-run behavior, rather than silently marking a backlog as read.
 */
export function unseenShares(projects, sharesSeenAt) {
  const mark = Number.isFinite(Number(sharesSeenAt)) ? Number(sharesSeenAt) : -1;
  return (Array.isArray(projects) ? projects : []).filter(
    (p) => isShare(p) && Number(p.memberSince) > mark
  );
}

export function unseenShareCount(projects, sharesSeenAt) {
  return unseenShares(projects, sharesSeenAt).length;
}

/**
 * The watermark to record after a list has been shown.
 *
 * THE GREATEST `memberSince` ACTUALLY DISPLAYED — never `Date.now()`. A wall
 * clock would cover shares created between the list load and the write, marking
 * them seen without them ever having been shown; the user would never learn
 * about that project. Returns null when there is nothing to advance to, so the
 * caller can skip the write entirely.
 */
export function watermarkFor(projects) {
  let max = null;
  for (const project of Array.isArray(projects) ? projects : []) {
    if (!isShare(project)) continue;
    const stamp = Number(project.memberSince);
    if (max == null || stamp > max) max = stamp;
  }
  return max;
}

/**
 * Should the client PATCH a new watermark?
 * Only when it moves forward — the server clamps with MAX() anyway, so this
 * just avoids a pointless round-trip on every switcher open.
 */
export function shouldAdvanceWatermark(projects, sharesSeenAt) {
  const next = watermarkFor(projects);
  if (next == null) return false;
  const current = Number.isFinite(Number(sharesSeenAt)) ? Number(sharesSeenAt) : -1;
  return next > current;
}
